import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import useChromeStyles from 'pages/schedule/SelectLessonTime/redesign/styles'
import useStyles from './styles'

// Same breakpoint the step 1 redesign uses to swap to its compact layout.
export const useIsCompact = () => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('sm'))
}

export const FORM_HEADING = "Who's taking the lesson?"

export const FormCard = ({ title = FORM_HEADING, subtitle, tip, children }) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  return (
    <section className={chrome.timeCard} aria-labelledby="select-student-title">
      <Typography
        variant="h5"
        component="h1"
        id="select-student-title"
        className={classes.heading}
      >
        {title}
      </Typography>
      {tip && <div className={classes.signInTip}>{tip}</div>}
      {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
      {children}
    </section>
  )
}

FormCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.node,
  tip: PropTypes.node,
  children: PropTypes.node,
}

export const InlineError = ({ children }) => {
  const classes = useStyles()
  if (!children) return null
  return (
    <div className={classes.inlineError} role="alert">
      <ErrorOutlineIcon aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}

InlineError.propTypes = { children: PropTypes.node }

const SubmitButton = ({ label, submitting, disabled }) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  return (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      className={clsx(chrome.continueButton, classes.submitButton)}
      disabled={disabled || submitting}
      aria-busy={submitting || undefined}
    >
      {submitting ? 'Continuing…' : label}
    </Button>
  )
}

SubmitButton.propTypes = {
  label: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
}

// <form> + form card + primary action. On desktop the button sits at the
// bottom of the card; on small screens it moves to the sticky bottom bar used
// by step 1 (below the card, still inside the form so Enter and the button both
// submit it).
export const FormShell = ({
  onSubmit,
  subtitle,
  tip,
  title,
  children,
  submitLabel,
  priceLabel,
  submitting,
  disabled,
  error,
  belowCard,
}) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  const isCompact = useIsCompact()
  const button = (
    <SubmitButton
      label={submitLabel}
      submitting={submitting}
      disabled={disabled}
    />
  )

  return (
    <form onSubmit={onSubmit} noValidate>
      <FormCard title={title} subtitle={subtitle} tip={tip}>
        {children}
        <InlineError>{error}</InlineError>
        {!isCompact && <div className={classes.actions}>{button}</div>}
      </FormCard>
      {isCompact && (
        <>
          {belowCard}
          <div className={chrome.bottomBar} data-testid="sticky-action-bar">
            <p className={chrome.bottomText}>
              {priceLabel ? `Total · ${priceLabel}` : 'Step 2 of 3'}
            </p>
            {button}
          </div>
        </>
      )}
    </form>
  )
}

FormShell.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  subtitle: PropTypes.node,
  tip: PropTypes.node,
  title: PropTypes.string,
  children: PropTypes.node,
  submitLabel: PropTypes.string.isRequired,
  priceLabel: PropTypes.string,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.node,
  belowCard: PropTypes.node,
}
